import * as Sentry from "@sentry/react";

const TOKEN_PARAM = /([?&]token=)[^&#\s"']*/gi;

/**
 * Redacts the value of any `token` query param, so a token-bearing URL (the
 * Ask NEO embed URL) never reaches Sentry. Safe to run on whole messages.
 */
export const scrubTokenFromUrl = (value: string): string =>
  value.replace(TOKEN_PARAM, "$1[redacted]");

const scrubRecord = <T extends Record<string, unknown>>(record: T): T => {
  const scrubbed: Record<string, unknown> = { ...record };

  for (const [key, value] of Object.entries(scrubbed)) {
    if (typeof value === "string") {
      scrubbed[key] = scrubTokenFromUrl(value);
    }
  }

  return scrubbed as T;
};

export const initSentry = () => {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn("Sentry DSN not provided, skipping Sentry initialization");
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      import.meta.env.VITE_ENV ||
      "development",
    release: `wbd-prototype@${new Date().toISOString().split("T")[0]}`,

    integrations: [
      // Browser tracing for performance monitoring.
      //
      // INP instrumentation is off: the web-vitals copy bundled into
      // @sentry-internal/browser-utils reads `interaction.entries[0].startTime`
      // without checking the list is populated, so a long interaction throws
      // "Cannot read properties of undefined (reading 'startTime')" out of the
      // PerformanceObserver callback. Re-enable once that dependency ships a fix.
      Sentry.browserTracingIntegration({ enableInp: false }),
      // Session replay (only on errors in production)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay sampling
    replaysSessionSampleRate: import.meta.env.PROD ? 0 : 0.1, // 0% in production for cost savings
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 1.0, // 100% on errors

    // Send default PII (user context)
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII,

    // Keep token-bearing URLs (the Ask NEO embed iframe) out of breadcrumbs
    // and performance spans.
    beforeBreadcrumb(breadcrumb) {
      return {
        ...breadcrumb,
        ...(breadcrumb.message !== undefined && {
          message: scrubTokenFromUrl(breadcrumb.message),
        }),
        ...(breadcrumb.data !== undefined && {
          data: scrubRecord(breadcrumb.data),
        }),
      };
    },
    beforeSendSpan(span) {
      return {
        ...span,
        ...(span.description !== undefined && {
          description: scrubTokenFromUrl(span.description),
        }),
        ...(span.data !== undefined && { data: scrubRecord(span.data) }),
      };
    },

    // Only send errors in non-local environments
    beforeSend(event, hint) {
      // Always log in development
      if (import.meta.env.DEV) {
        console.log("Sentry event:", event);
        console.log("Error hint:", hint.originalException);
      }

      // Don't send events in local environment
      if (import.meta.env.VITE_ENV === "local") {
        return null;
      }

      // Filter out some common non-critical errors
      const error = hint.originalException;
      if (error && error instanceof Error) {
        // Ignore network errors that might be expected
        if (error.message?.includes("Network request failed")) {
          return null;
        }
      }

      return event;
    },
  });
};
